import React, { useEffect } from "react";
import { BarChart, PieChart } from "@mui/x-charts";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from "@mui/joy";
import { useQuery, gql } from "@apollo/client";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const USER_OVERVIEW_QUERY = gql`
  query UserOverview {
    userOverview {
      users
      assetOwners
      backOfficeOfficials
      fieldOfficials
      others
      completedProfiles
      incompleteProfiles
      incompleteProfilesBreakdown {
        city
        country
        dateOfBirth
        employer
        employmentStatus
        firstName
        gender
        idPassportNumber
        lastName
        maritalStatus
        middleName
        nationality
        occupation
        postalCode
        stateProvince
        streetAddress
      }
      assetOwnersHavingAssets
      assetOwnersHavingValidAssets
      assetOwnersHavingExpiringSoonAssets
      assetOwnersHavingExpiredAssets
    }
  }
`;

const UserOverview = () => {
  const { data, loading, error, refetch } = useQuery(USER_OVERVIEW_QUERY);

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (
            wsData.type === "user_updated" ||
            wsData.type === "profile_updated" ||
            wsData.type === "asset_updated"
          ) {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          width: "100%",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography color="danger">
                Error loading user overview: {error?.message || "Unknown error"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Users by Role
            </Typography>
            <BarChart
              xAxis={[
                {
                  data: [
                    "Asset Owners",
                    "Back Office Officials",
                    "Field Officials",
                    "Others",
                  ],
                },
              ]}
              series={[
                {
                  data: [
                    data.userOverview.assetOwners,
                    data.userOverview.backOfficeOfficials,
                    data.userOverview.fieldOfficials,
                    data.userOverview.others,
                  ],
                },
              ]}
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Asset Owners by Asset Status
            </Typography>
            <BarChart
              xAxis={[
                {
                  data: [
                    "Having valid assets",
                    "Having expiring soon assets",
                    "Having expired assets",
                  ],
                },
              ]}
              series={[
                {
                  data: [
                    data.userOverview.assetOwnersHavingValidAssets,
                    data.userOverview.assetOwnersHavingExpiringSoonAssets,
                    data.userOverview.assetOwnersHavingExpiredAssets,
                  ],
                },
              ]}
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Incomplete Profiles by Missing Fields
            </Typography>
            <BarChart
              yAxis={[
                {
                  data: [
                    "City",
                    "Country",
                    "Date of Birth",
                    "Employer",
                    "Employment Status",
                    "First Name",
                    "Gender",
                    "ID/Passport Number",
                    "Last Name",
                    "Marital Status",
                    "Middle Name",
                    "Nationality",
                    "Occupation",
                    "Postal Code",
                    "State/Province",
                    "Street Address",
                  ],
                  scaleType: "band",
                },
              ]}
              series={[
                {
                  data: [
                    data.userOverview.incompleteProfilesBreakdown.city,
                    data.userOverview.incompleteProfilesBreakdown.country,
                    data.userOverview.incompleteProfilesBreakdown.dateOfBirth,
                    data.userOverview.incompleteProfilesBreakdown.employer,
                    data.userOverview.incompleteProfilesBreakdown
                      .employmentStatus,
                    data.userOverview.incompleteProfilesBreakdown.firstName,
                    data.userOverview.incompleteProfilesBreakdown.gender,
                    data.userOverview.incompleteProfilesBreakdown
                      .idPassportNumber,
                    data.userOverview.incompleteProfilesBreakdown.lastName,
                    data.userOverview.incompleteProfilesBreakdown.maritalStatus,
                    data.userOverview.incompleteProfilesBreakdown.middleName,
                    data.userOverview.incompleteProfilesBreakdown.nationality,
                    data.userOverview.incompleteProfilesBreakdown.occupation,
                    data.userOverview.incompleteProfilesBreakdown.postalCode,
                    data.userOverview.incompleteProfilesBreakdown.stateProvince,
                    data.userOverview.incompleteProfilesBreakdown.streetAddress,
                  ],
                  type: "bar",
                  color: "#1976d2",
                },
              ]}
              height={400}
              layout="horizontal"
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Key Metrics Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "success.500" }}>
                    {data.userOverview.users}
                  </Typography>
                  <Typography level="body-sm">Total Distinct Users</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "primary.500" }}>
                    {data.userOverview.completedProfiles}
                  </Typography>
                  <Typography level="body-sm">Completed Profiles</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "warning.500" }}>
                    {data.userOverview.incompleteProfiles}
                  </Typography>
                  <Typography level="body-sm">Incomplete Profiles</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserOverview;
